import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  CircularProgress,
  Collapse,
  Autocomplete
} from '@mui/material';
import {
  Devices as DevicesIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import DeviceTable from '../../../../../components/DeviceTable';
import { getLocationId } from '../../../utils/locationTransformers';

const DevicesTab = ({
  selectedLocation,
  devices,
  loadingDevices,
  onRefresh,
  onEditDevice,
  onDeleteDevice,
  onAddDevice,
  // Expected devices props
  expectedDevicesStatus,
  expectedDeviceTypes,
  expectedDevicesExpanded,
  setExpectedDevicesExpanded,
  savingExpectedDevices,
  allDeviceTypes,
  loadingDeviceTypes,
  handleAddExpectedDeviceType,
  handleRemoveExpectedDeviceType,
  handleUpdateMinCount
}) => {
  const locationId = getLocationId(selectedLocation);

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      {/* Expected Devices Section - Only for Unit locations (type 4) */}
      {selectedLocation.locationType === 4 && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ pb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }}
              onClick={() => setExpectedDevicesExpanded(!expectedDevicesExpanded)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DevicesIcon color="primary" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Expected Devices
                </Typography>
                {expectedDevicesStatus.length > 0 && (
                  <Chip
                    size="small"
                    label={`${expectedDevicesStatus.filter(e => e.fulfilled).length}/${expectedDevicesStatus.length}`}
                    color={expectedDevicesStatus.every(e => e.fulfilled) ? 'success' : 'warning'}
                  />
                )}
                {savingExpectedDevices && <CircularProgress size={16} sx={{ ml: 1 }} />}
              </Box>
              <IconButton size="small">
                {expectedDevicesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={expectedDevicesExpanded}>
              <Box sx={{ mt: 2 }}>
                <Autocomplete
                  size="small"
                  options={allDeviceTypes.filter(dt => !expectedDeviceTypes.some(e => e.deviceTypeId === dt.id))}
                  getOptionLabel={(option) => option.FrontEndName || option.friendlyName || `Type ${option.id}`}
                  loading={loadingDeviceTypes}
                  disabled={savingExpectedDevices}
                  onChange={(event, newValue) => {
                    if (newValue) handleAddExpectedDeviceType(newValue);
                  }}
                  value={null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add expected device type"
                      placeholder="Select a device type to add"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingDeviceTypes ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ mb: 2 }}
                />

                {expectedDevicesStatus.length > 0 ? (
                  <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
                    {expectedDevicesStatus.map((expected) => (
                      <ListItem
                        key={expected.id}
                        sx={{
                          borderLeft: 4,
                          borderColor: expected.fulfilled ? 'success.main' : 'error.main',
                          mb: 0.5,
                          bgcolor: 'background.paper',
                          borderRadius: '0 4px 4px 0',
                          pr: 12
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {expected.fulfilled ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={expected.name}
                          secondary={
                            expected.fulfilled
                              ? `${expected.actualCount}/${expected.minCount} present`
                              : `${expected.actualCount}/${expected.minCount} - Need ${expected.minCount - expected.actualCount} more`
                          }
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'absolute', right: 48 }}>
                          <Typography variant="caption" color="text.secondary">Min:</Typography>
                          <TextField
                            type="number"
                            size="small"
                            value={expected.minCount}
                            onChange={(e) => handleUpdateMinCount(expected.id, e.target.value)}
                            disabled={savingExpectedDevices}
                            inputProps={{ min: 1, style: { width: 40, textAlign: 'center', padding: '4px' } }}
                            sx={{ width: 60 }}
                          />
                        </Box>
                        <ListItemSecondaryAction>
                          <Tooltip title="Remove from expected">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemoveExpectedDeviceType(expected.id)}
                              disabled={savingExpectedDevices}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No expected device types defined. Add device types above to track what should be present in this unit.
                  </Typography>
                )}
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      )}

      <DeviceTable
        devices={devices}
        loading={loadingDevices}
        locationId={locationId}
        onRefresh={onRefresh}
        onEditDevice={onEditDevice}
        onDeleteDevice={onDeleteDevice}
        onAddDevice={onAddDevice}
      />
    </Box>
  );
};

export default DevicesTab;
